import { lazy, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SocialLoginProviderType } from '@shared/dream-components';
import { ButtonElement } from '@shared/dream-components/src/subscriptionManagementElements/shared/buttonElement/ButtonElement';
import { capitalize } from 'es-toolkit/string';

import type { Identity } from '~/interfaces/identity';
import type { Integration } from '~/interfaces/integration';

import ConnectedBadge from './ConnectedBadge';

const logoModules = import.meta.glob<{
  default: React.ComponentType<{ className?: string }>;
}>('./_logos/*.tsx');

const actionButtonModules = import.meta.glob<{
  default: React.ComponentType<{ identity: Identity }>;
}>('./_actionButtons/*.tsx');

const logoComponentCache = new Map<string, React.ComponentType<{ className?: string }>>();
const actionButtonComponentCache = new Map<string, React.ComponentType<{ identity: Identity }>>();

type BaseProps = {
  socialLoginProvider: Integration;
};

type ConnectedProps = BaseProps & {
  connectedIdentity: Identity;
  onDisconnect: (identity: Identity) => void;
  isDeleting?: boolean;
};

type DisconnectedProps = BaseProps & {
  connectedIdentity: null;
  onConnect: (provider: Integration) => void;
};

type Props = ConnectedProps | DisconnectedProps;

function connectedAccountLabel(identity: Identity): string | null {
  if (identity.provider === SocialLoginProviderType.SPOTIFY) {
    return identity.metadata.display_name || identity.uid;
  }

  return null;
}

const AccountConnection = (props: Props) => {
  const { t } = useTranslation('common', { keyPrefix: 'manage.account_profile.social_connections' });
  const { socialLoginProvider, connectedIdentity } = props;

  const formattedProvider = capitalize(socialLoginProvider.provider);

  const LogoComponent = useMemo(() => {
    const cachedComponent = logoComponentCache.get(formattedProvider);

    if (cachedComponent) {
      return cachedComponent;
    }

    const LazyComponent = lazy(() => {
      const modulePath = `./_logos/${formattedProvider}.tsx`;
      const moduleLoader = logoModules[modulePath];

      if (!moduleLoader) {
        throw new Error(`Logo component not found: ${formattedProvider}`);
      }

      return moduleLoader();
    });

    logoComponentCache.set(formattedProvider, LazyComponent);
    return LazyComponent;
  }, [formattedProvider]);

  const ActionButtonComponent = useMemo(() => {
    if (!connectedIdentity) return null;

    const cachedComponent = actionButtonComponentCache.get(formattedProvider);

    if (cachedComponent) {
      return cachedComponent;
    }

    const modulePath = `./_actionButtons/${formattedProvider}.tsx`;
    const moduleLoader = actionButtonModules[modulePath];

    if (!moduleLoader) {
      return null;
    }

    const LazyComponent = lazy(() => moduleLoader());

    actionButtonComponentCache.set(formattedProvider, LazyComponent);
    return LazyComponent;
  }, [formattedProvider, connectedIdentity]);

  const handleButtonClick = () => {
    if (connectedIdentity && 'onDisconnect' in props) {
      props.onDisconnect(connectedIdentity);
    } else if (!connectedIdentity && 'onConnect' in props) {
      props.onConnect(socialLoginProvider);
    }
  };

  const accountLabel = connectedIdentity ? connectedAccountLabel(connectedIdentity) : null;

  return (
    <div className="flex flex-row gap-2 items-start p-4 bg-white border border-gray-200 rounded-lg">
      <LogoComponent className="w-14 h-14 rounded-lg flex-shrink-0" />

      <div className="flex flex-row flex-1 min-w-0 gap-2">
        <div className="flex flex-col flex-1 min-w-0 gap-2">
          <div className="flex flex-row gap-2 bg-gray-50 rounded-md items-center px-3 min-h-10 py-2">
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium text-gray-900">{formattedProvider}</span>
              {accountLabel && <span className="text-xs text-gray-600 truncate">{accountLabel}</span>}
            </div>
            {connectedIdentity && <ConnectedBadge />}
          </div>

          <p className="text-gray-500 font-normal text-xs pl-2">
            {t('connect_provider_description', {
              provider: formattedProvider,
            })}
          </p>
        </div>

        <div className="flex flex-row gap-2">
          {ActionButtonComponent && connectedIdentity && <ActionButtonComponent identity={connectedIdentity} />}
          <ButtonElement
            variant={connectedIdentity ? 'default' : 'primary'}
            onClick={handleButtonClick}
            className="h-10"
            disabled={'isDeleting' in props ? props.isDeleting : false}
          >
            {connectedIdentity ? t('disconnect_label') : t('connect_label')}
          </ButtonElement>
        </div>
      </div>
    </div>
  );
};

export default AccountConnection;
